// packages/report-components/src/JumpContext.tsx
//
// "Jump to a section" as a capability the SURFACE provides, not something
// the components reach for.
//
// Before this package existed, `DataPoint` and `RecommendationList`
// imported `scrollToSection` from `apps/artifact-sandbox/src/runtime/scroll`
// directly, and `RecommendationList`'s "Evidence →" link additionally called
// `useOutlinePosition` to check the target exists. Both are facts about the
// reading room — a scrolling iframe with a registered outline — and neither
// is true on the report editor's canvas, where the blocks are cells in a
// grid and there is no outline to scroll to. So they become one context:
//
//   onJump(sectionId)      what to do when the reader activates a jump
//   hasSection(sectionId)  whether the surface knows that section at all
//
// The defaults are the canvas's answer: nothing happens, and no section is
// known. `hasSection` defaulting to FALSE is the conservative half — an
// "Evidence →" link that cannot go anywhere is a dead click, and the
// component's own documented rule has always been "unknown id → no link at
// all". The editor never passes `evidenceSectionId` anyway (plan Besluit 1),
// so on that surface this is belt and braces rather than a behaviour.
//
// The sandbox fills both in (`apps/artifact-sandbox/src/components/jump.tsx`)
// and its barrel wraps `DataPoint` / `RecommendationList` so a component
// rendered ANYWHERE in that app — including standalone in a test, and
// including authored JSX mounted outside the shell — gets the reading room's
// behaviour, byte-identical to before the cut.

import * as React from "react";

export type JumpContextValue = {
  /** Activate a jump to `sectionId`. Default: nothing happens. */
  onJump: (sectionId: string) => void;
  /** Does this surface know `sectionId`? Gates the affordance itself, so a
   *  surface without an outline shows no link rather than a dead one. */
  hasSection: (sectionId: string) => boolean;
};

const INERT: JumpContextValue = {
  onJump: (sectionId: string) => {
    // Not silent: a jump that goes nowhere is worth one line in the console
    // of whoever is looking at it. `debug`, not `warn` — on the editor's
    // canvas an inert jump is the DESIGN, not a defect (the browser gate in
    // this plan's E1 counts `console.error` only, for exactly this reason).
    if (typeof console !== "undefined") {
      console.debug(
        `[report-components] jump to ${JSON.stringify(sectionId)} ignored — ` +
          `this surface provides no JumpProvider.`,
      );
    }
  },
  hasSection: () => false,
};

const JumpContext = React.createContext<JumpContextValue>(INERT);

export type JumpProviderProps = {
  value: JumpContextValue;
  children?: React.ReactNode;
};

/** Give the subtree a way to jump between sections. Renders no DOM. */
export function JumpProvider({
  value,
  children,
}: JumpProviderProps): React.ReactElement {
  return <JumpContext.Provider value={value}>{children}</JumpContext.Provider>;
}

/** The surface's jump capability, or the inert default. */
export function useJump(): JumpContextValue {
  return React.useContext(JumpContext);
}
